import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { doInitLU, luHandleAppiError, type U3302InitLU } from './U3302Service';
import { U3302ContextProvider } from './U3302Context';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export const NS = 'u3302';

export const U3302ROLES = {
  READ: 'U3302:READ',
  ADD: 'U3302:ADD',
  EDIT: 'U3302:EDIT',
  DELETE: 'U3302:DELETE',
  AUDITS: 'U3302:AUDITS',
} as const;
export type U3302ROLES = (typeof U3302ROLES)[keyof typeof U3302ROLES];

const defaultInitLU: U3302InitLU = {
  launchSearch: true,
  hideSearch: false,
  nbRecordPerPage: 50,
  categoryOrigins: ['SYSTEM', 'MANUAL'],
};

const U3302: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3302InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3302'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3302InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3302InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        categoryOrigins: initLU.categoryOrigins ?? defaultInitLU.categoryOrigins,
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3302ContextProvider initLU={initLUData}>
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </U3302ContextProvider>
  );
};

export default U3302;
